/* =========================================================================
   Carriage & Co. — Shopify Theme Interactions
   Vanilla JS. Modules are self-contained; safe on any template.
   ========================================================================= */

(function () {
  'use strict';

  const $  = (sel, ctx = document) => ctx.querySelector(sel);
  const $$ = (sel, ctx = document) => Array.from(ctx.querySelectorAll(sel));

  /* ---------------------------------------------------------------
     Toast (shared feedback)
     --------------------------------------------------------------- */
  function showToast(message) {
    let toast = document.getElementById('cc-toast');
    if (!toast) {
      toast = document.createElement('div');
      toast.id = 'cc-toast';
      toast.setAttribute('role', 'status');
      toast.setAttribute('aria-live', 'polite');
      Object.assign(toast.style, {
        position: 'fixed', top: '84px', right: '20px',
        background: 'var(--c-charcoal)', color: 'var(--c-cream)',
        padding: '14px 22px', borderRadius: '4px',
        fontSize: '13px', letterSpacing: '0.06em', textTransform: 'uppercase',
        transform: 'translateX(130%)',
        transition: 'transform 320ms cubic-bezier(0.16,1,0.3,1)',
        zIndex: '300', maxWidth: '320px',
        boxShadow: '0 4px 24px rgba(0,0,0,0.10)'
      });
      document.body.appendChild(toast);
    }
    toast.textContent = message;
    requestAnimationFrame(() => { toast.style.transform = 'translateX(0)'; });
    clearTimeout(toast._t);
    toast._t = setTimeout(() => { toast.style.transform = 'translateX(130%)'; }, 2800);
  }

  /* ---------------------------------------------------------------
     Cart count badge refresh (from Shopify cart.js)
     --------------------------------------------------------------- */
  async function refreshCartCount() {
    try {
      const res = await fetch('/cart.js', { headers: { 'Accept': 'application/json' } });
      if (!res.ok) return;
      const cart = await res.json();
      $$('[data-cart-count]').forEach(el => {
        el.textContent = cart.item_count;
        el.hidden = cart.item_count === 0;
      });
    } catch (e) { /* silent */ }
  }

  /* ---------------------------------------------------------------
     Mobile menu drawer
     --------------------------------------------------------------- */
  function initMenu() {
    const openBtn  = $('[data-menu-open]');
    const closeBtn = $('[data-menu-close]');
    const drawer   = $('#menu-drawer');
    if (!openBtn || !drawer) return;

    const open = () => {
      drawer.setAttribute('aria-hidden', 'false');
      openBtn.setAttribute('aria-expanded', 'true');
      document.body.style.overflow = 'hidden';
      const firstLink = $('.menu__nav a', drawer);
      if (firstLink) firstLink.focus();
    };
    const close = () => {
      drawer.setAttribute('aria-hidden', 'true');
      openBtn.setAttribute('aria-expanded', 'false');
      document.body.style.overflow = '';
      openBtn.focus();
    };

    openBtn.addEventListener('click', open);
    closeBtn && closeBtn.addEventListener('click', close);
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && drawer.getAttribute('aria-hidden') === 'false') close();
    });
    $$('.menu__nav a', drawer).forEach(a => a.addEventListener('click', close));
  }

  /* ---------------------------------------------------------------
     Accordion
     --------------------------------------------------------------- */
  function initAccordion() {
    $$('.acc__item').forEach(item => {
      const trigger = $('.acc__trigger', item);
      const panel   = $('.acc__panel', item);
      if (!trigger || !panel) return;

      const setHeight = (open) => {
        panel.style.maxHeight = open ? panel.scrollHeight + 'px' : '0px';
      };
      setHeight(item.dataset.open === 'true');

      trigger.addEventListener('click', () => {
        const isOpen = item.dataset.open === 'true';
        item.dataset.open = String(!isOpen);
        trigger.setAttribute('aria-expanded', String(!isOpen));
        setHeight(!isOpen);
      });
    });

    let t;
    window.addEventListener('resize', () => {
      clearTimeout(t);
      t = setTimeout(() => {
        $$('.acc__item[data-open="true"] .acc__panel').forEach(p => {
          p.style.maxHeight = p.scrollHeight + 'px';
        });
      }, 120);
    });
  }

  /* ---------------------------------------------------------------
     Product gallery — thumbs swap the main image; swiping the main
     image (finger, pen or mouse drag) or the arrows move to the
     next/previous photo; a counter shows the position
     --------------------------------------------------------------- */
  function initGallery() {
    const main = $('#gallery-main');
    const mainImg = $('#gallery-main-img');
    const thumbs = $$('.gallery__thumb');
    if (!thumbs.length) return;

    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    let current = Math.max(0, thumbs.findIndex(t => t.getAttribute('aria-selected') === 'true'));
    const countEl = main ? $('[data-gallery-current]', main) : null;
    const strip = thumbs[0].parentElement;

    // On phones the thumbnails sit in one row that scrolls sideways: keep the active one in view
    const keepThumbInView = (t) => {
      if (!strip || strip.scrollWidth <= strip.clientWidth + 1) return;
      const s = strip.getBoundingClientRect();
      const r = t.getBoundingClientRect();
      const left = strip.scrollLeft + (r.left - s.left) - (strip.clientWidth - r.width) / 2;
      strip.scrollTo({ left: Math.max(0, left), behavior: reduceMotion ? 'auto' : 'smooth' });
    };

    // Loads a neighbouring photo in advance so the swipe doesn't flash
    const preload = (i) => {
      const t = thumbs[(i + thumbs.length) % thumbs.length];
      if (t && t.dataset.src && !t._preloaded) {
        const img = new Image();
        img.src = t.dataset.src;
        t._preloaded = true;
      }
    };

    const show = (i) => {
      const n = thumbs.length;
      current = ((i % n) + n) % n;
      thumbs.forEach((t, k) => t.setAttribute('aria-selected', String(k === current)));
      const t = thumbs[current];
      if (mainImg && t.dataset.src) {
        mainImg.src = t.dataset.src;
        mainImg.alt = t.dataset.alt || '';
      }
      if (countEl) countEl.textContent = String(current + 1);
      keepThumbInView(t);
      preload(current + 1);
      preload(current - 1);
    };

    thumbs.forEach((thumb, i) => {
      thumb.addEventListener('click', () => show(i));
    });

    // Swipe on the main image
    if (!main || !mainImg || thumbs.length < 2) return;
    preload(current + 1);
    preload(current - 1);

    mainImg.setAttribute('draggable', 'false');
    mainImg.addEventListener('dragstart', (e) => e.preventDefault());

    let pointerId = null, startX = 0, startY = 0, dx = 0;
    let tracking = false, decided = false, horizontal = false, busy = false;

    const move = (x, animate) => {
      mainImg.style.transition = (animate && !reduceMotion)
        ? 'transform 200ms var(--ease-out), opacity 200ms var(--ease-out)'
        : 'none';
      mainImg.style.transform = x ? 'translateX(' + x + 'px)' : '';
    };

    // dir = 1 goes to the next photo, -1 to the previous one (wraps around)
    const goTo = (dir) => {
      if (reduceMotion) { move(0, false); show(current + dir); return; }
      busy = true;
      const shift = Math.max(40, main.clientWidth * 0.25);
      move(-dir * shift, true);
      mainImg.style.opacity = '0';
      setTimeout(() => {
        show(current + dir);
        move(dir * shift, false);
        requestAnimationFrame(() => requestAnimationFrame(() => {
          move(0, true);
          mainImg.style.opacity = '';
          setTimeout(() => { busy = false; }, 220);
        }));
      }, 200);
    };

    // Arrows (shown on computers only, via CSS)
    const prevBtn = $('[data-gallery-prev]', main);
    const nextBtn = $('[data-gallery-next]', main);
    if (prevBtn) prevBtn.addEventListener('click', () => { if (!busy) goTo(-1); });
    if (nextBtn) nextBtn.addEventListener('click', () => { if (!busy) goTo(1); });

    main.addEventListener('pointerdown', (e) => {
      if (busy || (e.pointerType === 'mouse' && e.button !== 0)) return;
      if (e.target.closest('[data-gallery-prev], [data-gallery-next]')) return;
      pointerId = e.pointerId;
      startX = e.clientX;
      startY = e.clientY;
      dx = 0;
      tracking = true;
      decided = false;
      horizontal = false;
    });

    main.addEventListener('pointermove', (e) => {
      if (!tracking || e.pointerId !== pointerId) return;
      const mx = e.clientX - startX;
      const my = e.clientY - startY;
      if (!decided) {
        if (Math.abs(mx) < 8 && Math.abs(my) < 8) return;
        decided = true;
        horizontal = Math.abs(mx) > Math.abs(my);
        if (!horizontal) { tracking = false; return; }   // vertical: let the page scroll
        try { main.setPointerCapture(pointerId); } catch (_) { /* older browsers */ }
      }
      dx = mx;
      move(dx, false);
    });

    const end = (e) => {
      if (!tracking || e.pointerId !== pointerId) return;
      tracking = false;
      if (!horizontal) return;
      const threshold = Math.min(60, main.clientWidth * 0.15);
      if (e.type !== 'pointercancel' && Math.abs(dx) > threshold) {
        goTo(dx < 0 ? 1 : -1);
      } else {
        move(0, true);
      }
      dx = 0;
    };

    main.addEventListener('pointerup', end);
    main.addEventListener('pointercancel', end);
  }

  /* ---------------------------------------------------------------
     Variant picker — maps selected options to a Shopify variant
     Requires: #product-json (application/json), form [name=id],
     option buttons with [data-option-index][data-value]
     --------------------------------------------------------------- */
  const COLOUR_MAP = {
    'soft beige': '#E4D3C1', 'beige': '#E4D3C1', 'warm taupe': '#C1A98F',
    'taupe': '#C1A98F', 'charcoal': '#3A3833', 'cream': '#F5F1EA',
    'black': '#1E1D1B', 'white': '#FFFFFF', 'nude': '#D9BFA8',
    'complexion': '#E8D3C0', 'skin': '#E8D3C0', 'grey': '#9B9B94',
    'gray': '#9B9B94', 'pink': '#E8C8C4', 'blue': '#A9B8C4',
    'apricot': '#F0C8A8', 'khaki': '#B5A98C'
  };

  function initVariantPicker() {
    const jsonEl = $('#product-json');
    const form = $('#product-form');
    if (!jsonEl || !form) return;

    let product;
    try { product = JSON.parse(jsonEl.textContent); } catch (e) { return; }

    const idInput = $('input[name="id"]', form);
    const priceEls = $$('[data-price]');
    const addBtns = $$('[data-add-to-bag]');
    const stickyVariant = $('#sticky-variant');

    // Paint swatch dots where the colour is known
    $$('.swatch-dot').forEach(dot => {
      const c = (dot.dataset.colour || '').toLowerCase().trim();
      if (COLOUR_MAP[c]) {
        dot.style.background = COLOUR_MAP[c];
      } else {
        dot.hidden = true;
      }
    });

    // Variant options come as option1/option2/option3 in Shopify's JSON
    const getOpts = (v) => [v.option1, v.option2, v.option3].filter(o => o !== null && o !== undefined);

    // Current selection: from the variant preselected in the form
    const currentVariant = product.variants.find(v => String(v.id) === String(idInput.value)) || product.variants[0];
    const selected = currentVariant ? getOpts(currentVariant) : [];

    const money = (cents) => {
      const amount = (cents / 100);
      const formatted = (amount % 1 === 0) ? amount.toFixed(0) : amount.toFixed(2);
      return '£' + formatted;
    };

    const syncButtons = () => {
      $$('.selector [data-option-index]').forEach(btn => {
        const idx = parseInt(btn.dataset.optionIndex, 10);
        const pressed = selected[idx] === btn.dataset.value;
        btn.setAttribute('aria-pressed', String(pressed));
      });
      // Update visible colour name label if present
      const colourLabel = $('#colour-name');
      if (colourLabel) {
        const colourIdx = product.options.findIndex(o => /colou?r/i.test(o.name || o));
        if (colourIdx > -1 && selected[colourIdx]) colourLabel.textContent = selected[colourIdx];
      }
    };

    const findVariant = () =>
      product.variants.find(v => {
        const opts = getOpts(v);
        return opts.length === selected.length && opts.every((opt, i) => opt === selected[i]);
      });

    const colourOptionIdx = product.options.findIndex(o => /colou?r/i.test(o.name || o));

    // syncImage: true when the colour changed (or on load); a size change keeps the photo in view
    const applyVariant = (syncImage) => {
      const v = findVariant();
      if (!v) return;
      idInput.value = v.id;
      priceEls.forEach(el => { el.textContent = money(v.price); });
      addBtns.forEach(btn => {
        btn.disabled = !v.available;
        const label = btn.querySelector('[data-btn-label]');
        if (label) {
          label.textContent = v.available ? ('Add to bag · ' + money(v.price)) : 'Sold out';
        }
      });
      if (stickyVariant) stickyVariant.textContent = v.title.replace(/\s\/\s/g, ' · ');

      // Galeria acompanha a cor selecionada (só quando a cor muda, não o tamanho)
      if (!syncImage) return;
      const vSrc = v.featured_image && v.featured_image.src;
      if (!vSrc) return;
      const key = (u) => (u || '').split('?')[0].split('/').pop()
                    .replace(/_\d+x\d*(@\dx)?(_crop_\w+)?(?=\.\w+$)/, '');
      const thumbs = $$('.gallery__thumb');
      const match = thumbs.find(t => key(t.dataset.src) === key(vSrc));
      if (match) {
        match.click();
      } else {
        const mainImg = $('#gallery-main-img');
        if (mainImg) {
          mainImg.src = vSrc;
          if (v.featured_image.alt) mainImg.alt = v.featured_image.alt;
        }
      }
    };

    $$('.selector [data-option-index]').forEach(btn => {
      btn.addEventListener('click', () => {
        const idx = parseInt(btn.dataset.optionIndex, 10);
        selected[idx] = btn.dataset.value;
        syncButtons();
        applyVariant(idx === colourOptionIdx);
      });
    });

    syncButtons();
    applyVariant(true);
  }

  /* ---------------------------------------------------------------
     AJAX add-to-cart (progressive enhancement over normal submit)
     --------------------------------------------------------------- */
  function initAjaxCart() {
    const form = $('#product-form');
    if (!form) return;

    let ajaxFailed = false;
    form.addEventListener('submit', async (e) => {
      if (ajaxFailed) return; // native submit fallback
      e.preventDefault();
      const btn = $('[data-add-to-bag]', form);
      if (btn) { btn.style.transform = 'scale(0.97)'; setTimeout(() => btn.style.transform = '', 140); }

      try {
        const res = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Accept': 'application/json' },
          body: new FormData(form)
        });
        if (res.ok) {
          showToast('Added to bag ✓');
          refreshCartCount();
        } else {
          const err = await res.json().catch(() => ({}));
          showToast(err.description || 'Could not add to bag');
        }
      } catch (_) {
        // Network hiccup — fall back to a normal (non-AJAX) submit
        ajaxFailed = true;
        form.submit();
      }
    });

    // Sticky mobile CTA proxies the main form submit
    const stickyBtn = $('[data-sticky-add]');
    if (stickyBtn) {
      stickyBtn.addEventListener('click', () => {
        if (typeof form.requestSubmit === 'function') form.requestSubmit();
        else form.submit();
      });
    }
  }

  /* ---------------------------------------------------------------
     Sticky bottom CTA (mobile, product page)
     --------------------------------------------------------------- */
  function initStickyCTA() {
    const cta = $('#sticky-cta');
    const hero = $('.p-hero');
    if (!cta || !hero) return;

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        cta.setAttribute('aria-hidden', String(entry.isIntersecting));
      });
    }, { threshold: 0.1 });

    observer.observe(hero);
  }

  /* ---------------------------------------------------------------
     Reveal on scroll
     --------------------------------------------------------------- */
  function initReveal() {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    const targets = [
      ...$$('.manifesto__title'),
      ...$$('.how__card'),
      ...$$('.voice'),
      ...$$('.under__tile'),
      ...$$('.compare__col')
    ];
    targets.forEach(el => el.classList.add('reveal'));

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.setAttribute('data-inview', 'true');
          observer.unobserve(entry.target);
        }
      });
    }, { threshold: 0.1, rootMargin: '0px 0px -50px 0px' });

    targets.forEach(el => observer.observe(el));
  }

  /* ---------------------------------------------------------------
     Cart page — auto-submit quantity changes
     --------------------------------------------------------------- */
  function initCartPage() {
    const cartForm = $('#cart-form');
    if (!cartForm) return;
    $$('.qty input', cartForm).forEach(input => {
      input.addEventListener('change', () => cartForm.submit());
    });
  }

  /* ---------------------------------------------------------------
     Init
     --------------------------------------------------------------- */
  const init = () => {
    initMenu();
    initAccordion();
    initGallery();
    initVariantPicker();
    initAjaxCart();
    initStickyCTA();
    initReveal();
    initCartPage();
    refreshCartCount();
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
